/**
 * Hook React pour les signatures électroniques
 * Utilise TanStack Query (React Query) pour la gestion du cache et des états
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { signaturesAPI } from '../services/signatures.service';
import type { CreateSignatureInput, SignatureFilters } from '../types/signatures.types';
import { toast } from 'react-hot-toast';
import { notifications } from '@/lib/notifications';
import { getErrorMessage } from '@/lib/errorHandler';

/**
 * Hook pour créer une signature
 */
export const useCreateSignature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSignatureInput) => signaturesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signatures'] });
      toast.success('Signature créée avec succès');
    },
    onError: (error: Error) => {
      notifications.error(getErrorMessage(error));
    },
  });
};

/**
 * Hook pour obtenir une signature par ID
 */
export const useSignature = (id: string, enabled = true) => {
  return useQuery({
    queryKey: ['signatures', id],
    queryFn: () => signaturesAPI.getById(id),
    enabled: enabled && !!id,
  });
};

/**
 * Hook pour obtenir la signature d'une entité
 */
export const useEntitySignature = (
  entityType: string,
  entityId: string,
  enabled = true
) => {
  return useQuery({
    queryKey: ['signatures', 'entity', entityType, entityId],
    queryFn: () => signaturesAPI.getByEntity(entityType, entityId),
    enabled: enabled && !!entityType && !!entityId,
    retry: false, // Ne pas retry si la signature n'existe pas
  });
};

/**
 * Hook pour obtenir toutes les signatures d'une entité (historique)
 */
export const useEntitySignatures = (
  entityType: string,
  entityId: string,
  enabled = true
) => {
  return useQuery({
    queryKey: ['signatures', 'entity', entityType, entityId, 'all'],
    queryFn: () => signaturesAPI.getAllByEntity(entityType, entityId),
    enabled: enabled && !!entityType && !!entityId,
  });
};

/**
 * Hook pour lister les signatures avec filtres
 */
export const useSignatures = (filters?: SignatureFilters) => {
  return useQuery({
    queryKey: ['signatures', 'list', filters],
    queryFn: () => signaturesAPI.list(filters),
  });
};

/**
 * Hook pour générer le PDF de preuve
 */
export const useGenerateProofPdf = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (signatureId: string) => signaturesAPI.generateProofPdf(signatureId),
    onSuccess: (data, signatureId) => {
      queryClient.invalidateQueries({ queryKey: ['signatures', signatureId] });
      toast.success('PDF de preuve généré avec succès');
    },
    onError: (error: Error) => {
      notifications.error(getErrorMessage(error));
    },
  });
};

/**
 * Hook pour obtenir les statistiques de signature
 */
export const useSignatureStats = () => {
  return useQuery({
    queryKey: ['signatures', 'stats'],
    queryFn: () => signaturesAPI.getStats(),
  });
};

/**
 * Hook pour supprimer une signature (ADMIN seulement)
 */
export const useDeleteSignature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => signaturesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signatures'] });
      toast.success('Signature supprimée');
    },
    onError: (error: Error) => {
      notifications.error(getErrorMessage(error));
    },
  });
};

/**
 * Hook complet pour gérer les signatures
 */
export const useSignaturesManagement = (filters?: SignatureFilters) => {
  const { data: list, isLoading, error, refetch } = useSignatures(filters);
  const createMutation = useCreateSignature();
  const deleteMutation = useDeleteSignature();
  const generateProofMutation = useGenerateProofPdf();

  return {
    signatures: list?.signatures || [],
    total: list?.total || 0,
    isLoading,
    error,
    refetch,
    createSignature: createMutation.mutate,
    isCreating: createMutation.isPending,
    deleteSignature: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    generateProof: generateProofMutation.mutate,
    isGenerating: generateProofMutation.isPending,
  };
};
